import React, { useEffect, useState } from "react";

import { useTranslation, I18nextProvider } from "react-i18next";
import instance from "../../utils/i18n";

import EsriSearch from "@arcgis/core/widgets/Search";
import { StyledSearchWrapper, StyledSearchWrapperText } from "./Search-styled";
import { SearchProps, LocatorSourceInfo } from "./Search.types";
import { _getFeatureLayerSource, _getLocatorSource, _handleSearchComplete } from "./SearchHelper";

import "@esri/calcite-components/dist/components/calcite-button";
import "@esri/calcite-components/dist/components/calcite-icon";
import { CalciteButton, CalciteIcon } from "@esri/calcite-components-react";

import "./Search.css";

const Search = ({
  mapView,
  sourceInfos,
  searchTerm,
  isLanding,
  onSearchStart,
  onSearchClear,
  onSearchComplete,
  onSearchBlocked,
  onSearchError,
  appId,
  onEvacZoneUpdate,
}: SearchProps) => {
  const [search, setSearch] = useState<EsriSearch | undefined>();
  const [isMyLocLoading, setIsMyLocLoading] = useState<boolean>(false);

  const { t, i18n } = useTranslation("components", { i18n: instance });

  useEffect(() => {
    const createSearch = () => {
      const sources = sourceInfos?.map((info) => {
        switch (info.type) {
          case "layer":
            return _getFeatureLayerSource(info, t);
          case "locator":
            return _getLocatorSource(info, t);
          default:
            throw new Error(t("search.sourceInfoError"));
        }
      });
      const newSearch = new EsriSearch({
        view: mapView,
        container: "searchDiv",
        sources: sources,
        searchTerm,
        includeDefaultSources: false,
      });

      newSearch.on("search-start", () => {
        onSearchStart?.();
      });

      newSearch.on("search-complete", (e) => {
        // console.log('search complete', encodeURIComponent)
        _handleSearchComplete(e, onSearchComplete, onSearchError);
      });

      newSearch.on("search-clear", () => {
        onSearchClear?.();
      });

      newSearch.on("select-result", function (event) {
        // console.log('select-result')
        // console.log('appId:', appId)
        if (appId && appId === "hurricane") {
          const result = event.result;
          // console.log("Selected result:", result);

          // Full feature and its attributes
          const attributes = result.feature?.attributes;
          // console.log("Attributes:", attributes);

          onEvacZoneUpdate?.(+attributes.hurricaneEvacuationZone);
        }
      });

      setSearch(newSearch);
    };

    if (!search && sourceInfos) {
      createSearch();
    }
    // including search & searchTerm causes infinate loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapView, onSearchClear, onSearchStart, sourceInfos]);

  useEffect(() => {
    if (search) {
      search.searchTerm = searchTerm;
    }
  }, [search, searchTerm]);

  /*
  useEffect(() => {
    // shift focus to search widget after it has been initialized
    search?.when(() => {
      search?.focus();
    });
  }, [search]);
  */

  // Reverse geocode the device location using the configured locator source.
  // This helper constructs a reverseGeocode REST request directly rather than
  // relying on ArcGIS helper modules that may not match the runtime locator URL.
  const reverseGeocode = async (position: GeolocationPosition) => {
    const locatorInfo = sourceInfos?.find((info): info is LocatorSourceInfo => info.type === "locator");
    if (!locatorInfo) {
      return undefined;
    }

    try {
      // Start from the configured locator URL, which points at a GeocodeServer and
      // may already contain required auth/query params such as a subscription key.
      const locatorUrl = new URL(locatorInfo.url);

      // Reverse geocoding is a separate REST operation under the same service.
      // If the configured URL is .../GeocodeServer, this becomes .../GeocodeServer/reverseGeocode.
      // If we decide to go with OTI Locator, this would be .../NYC_OTI_Locator/GeocodeServer/reverseGeocode instead.
      const reverseGeocodeUrl = new URL(
        `${locatorUrl.origin}${locatorUrl.pathname.replace(/\/+$/, "")}/reverseGeocode`
      );

      // Copy any existing query params from the original locator URL so auth details
      // continue to work on the reverseGeocode request.
      locatorUrl.searchParams.forEach((value, key) => reverseGeocodeUrl.searchParams.append(key, value));

      // ArcGIS REST expects JSON output plus the point as "longitude,latitude".
      // We also ask for no geometry because the UI only needs the address string.
      reverseGeocodeUrl.searchParams.set("f", "json");
      reverseGeocodeUrl.searchParams.set("location", `${position.coords.longitude},${position.coords.latitude}`);
      reverseGeocodeUrl.searchParams.set("outFields", "*");
      reverseGeocodeUrl.searchParams.set("returnGeometry", "false");

      // Execute the reverse geocode request against the locator service.
      const response = await fetch(reverseGeocodeUrl.toString(), { method: "GET" });
      if (!response.ok) {
        throw new Error(`Reverse geocode HTTP ${response.status}`);
      }

      // ArcGIS can return a 200 with an error payload, so check both HTTP and JSON.
      const data = await response.json();
      if (data?.error) {
        throw new Error(data.error.message || "Reverse geocode failed");
      }

      // Different locator services populate different address fields, so prefer the
      // most complete labels first and then fall back through shorter alternatives.
      return (
        data?.address?.LongLabel ||
        data?.address?.ShortLabel ||
        data?.address?.Address ||
        data?.address?.Match_addr ||
        data?.address?.Neighborhood ||
        data?.attributes?.Match_addr ||
        undefined
      );
    } catch (error) {
      console.warn("Reverse geocode failed", error);
      return undefined;
    }
  };

  const onLocate = () => {
    if (navigator.geolocation) {
      setIsMyLocLoading(true);
      onSearchStart?.();
      navigator.geolocation.getCurrentPosition(
        async (event) => {
          const address = await reverseGeocode(event);
          // If reverse geocoding fails, keep a user-facing fallback that explains
          // the location was found but a street address could not be resolved.
          const searchValue = address || `${t("search.nearMe")} (address unavailable)`;
          if (search) {
            search.searchTerm = searchValue;
          }
          setIsMyLocLoading(false);
          onSearchComplete?.({ searchTerm: searchValue, position: event });
        },
        (error) => {
          console.warn(t("search.geolocateError", { error: error }));
          setIsMyLocLoading(false);
          onSearchBlocked?.();
        }
      );
    }
  };

  return (
    <I18nextProvider i18n={i18n}>
      <StyledSearchWrapper>
        <div id="searchDiv" />
        {isLanding ? <StyledSearchWrapperText>{t("search.or")}</StyledSearchWrapperText> : null}
        <CalciteButton
          id="myloc-btn"
          appearance="outline-fill"
          kind="neutral"
          label={t("search.viewNearbyLocs")}
          round={isLanding}
          scale="m"
          onClick={onLocate}
          data-testid="btnLocateme"
          loading={isMyLocLoading || undefined}
        >
          <CalciteIcon icon="gps-on" scale="s"></CalciteIcon>
          {isLanding ? t("search.nearby") : ""}
        </CalciteButton>
      </StyledSearchWrapper>
    </I18nextProvider>
  );
};

export default React.memo(Search);
